import Phaser from 'phaser';

import { Direction } from '@shared/types/entity';
import { GAME_FONT_FAMILY } from '@shared/constants';

import { getBodySpriteKey, getBodyIdleFrame } from '../../sprites/BodySpriteManager';
import { getHeadSpriteKey, getHeadFrameForDirection } from '../../sprites/HeadSpriteManager';
import { getShieldSpriteKey, getShieldIdleFrame } from '../../sprites/ShieldSpriteManager';
import { getWeaponSpriteKey, getWeaponIdleFrame } from '../../sprites/WeaponSpriteManager';

/**
 * Helper para agregar la fuente del juego y resolución alta a un estilo de texto.
 * La resolución alta hace que los textos se vean nítidos incluso con pixelArt: true.
 */
function withGameFont<T extends Phaser.Types.GameObjects.Text.TextStyle>(style?: T): T {
  return {
    fontFamily: GAME_FONT_FAMILY,
    resolution: 2, // Resolución alta para textos nítidos
    ...style,
  } as T;
}

/**
 * Entrada de la lista del InvokePanel. Puede ser un header de sección
 * (no clickeable / no seleccionable) o un item con nombre del NPC.
 * Los headers visualmente se renderizan distinto pero comparten la misma
 * fila del grid de 24 slots; el click sobre un header lo ignora el caller.
 */
export type InvokeListEntry =
  | { type: 'header'; label: string }
  | { type: 'item'; name: string };

type InvokePanelCallbacks = {
  onSelect(index: number): void;
  onInvoke(): void;
  onMove(x: number, y: number): void;
};

type InvokePanelOptions = {
  initialX: number;
  initialY: number;
  width: number;
  height: number;
};

type InvokePanelElements = {
  bg: Phaser.GameObjects.Rectangle;
  listBg: Phaser.GameObjects.Rectangle;
  title: Phaser.GameObjects.Text;
  list: Phaser.GameObjects.Text[];
  nameText: Phaser.GameObjects.Text;
  detailText: Phaser.GameObjects.Text;
  invokeButton: Phaser.GameObjects.Text;
  previewBox: Phaser.GameObjects.Rectangle;
  previewBody: Phaser.GameObjects.Sprite;
  previewHead: Phaser.GameObjects.Sprite;
  previewShield: Phaser.GameObjects.Sprite;
  previewWeapon: Phaser.GameObjects.Sprite;
  closeButton: Phaser.GameObjects.Text;
  panelX: number;
  panelY: number;
  panelWidth: number;
  panelHeight: number;
};

export class InvokePanel {
  private readonly scene: Phaser.Scene;
  private readonly callbacks: InvokePanelCallbacks;
  private elements: InvokePanelElements | null = null;

  private isOpen = false;
  private isDragging = false;
  private dragOffsetX = 0;
  private dragOffsetY = 0;

  constructor(scene: Phaser.Scene, callbacks: InvokePanelCallbacks, opts: InvokePanelOptions) {
    this.scene = scene;
    this.callbacks = callbacks;
    this.buildPanel(opts);
    this.setVisible(false);
  }

  open(): void {
    this.setVisible(true);
    this.isOpen = true;
  }

  close(): void {
    this.setVisible(false);
    this.isOpen = false;
  }

  toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  isOpenPanel(): boolean {
    return this.isOpen;
  }

  isDraggingPanel(): boolean {
    return this.isDragging;
  }

  moveTo(x: number, y: number): void {
    this.move(x, y);
    this.callbacks.onMove(x, y);
  }

  handlePointerMove(pointer: Phaser.Input.Pointer): void {
    if (!this.isDragging || !this.elements) return;
    const newX = pointer.x - this.dragOffsetX;
    const newY = pointer.y - this.dragOffsetY;
    this.move(newX, newY);
    this.callbacks.onMove(newX, newY);
  }

  handlePointerUp(pointer: Phaser.Input.Pointer): void {
    if (this.isDragging) {
      this.isDragging = false;
      pointer.event.stopPropagation();
    }
  }

  refreshList(
    entries: InvokeListEntry[],
    selectedRowIndex: number,
    detail: string,
    selectedName: string
  ): void {
    if (!this.elements) return;
    this.elements.list.forEach((txt, i) => {
      const entry = entries[i];
      if (!entry) {
        txt.setText('');
        txt.setVisible(false);
        txt.setActive(false);
        return;
      }
      if (entry.type === 'header') {
        txt.setText(`— ${entry.label} —`);
        txt.setVisible(this.isOpen);
        txt.setActive(true);
        // Header: color distinto, bold; el click no selecciona (el caller filtra).
        txt.setStyle({ color: '#88aaff', fontStyle: 'bold' });
      } else {
        txt.setText(entry.name);
        txt.setVisible(this.isOpen);
        txt.setActive(true);
        txt.setStyle({
          color: i === selectedRowIndex ? '#ffffaa' : '#ffffff',
          fontStyle: 'normal',
        });
      }
    });

    this.elements.nameText.setText(selectedName);
    this.elements.detailText.setText(detail || 'Selecciona un NPC');
  }

  refreshPreview(bodyId: number, headId: number, shieldId: number, weaponId: number): void {
    if (!this.elements) return;
    const { previewBody, previewHead, previewShield, previewWeapon } = this.elements;

    previewBody
      .setTexture(getBodySpriteKey(bodyId))
      .setFrame(getBodyIdleFrame(Direction.DOWN, bodyId))
      .setVisible(this.isOpen);

    if (headId > 0) {
      previewHead
        .setTexture(getHeadSpriteKey(headId))
        .setFrame(getHeadFrameForDirection(headId, Direction.DOWN))
        .setVisible(this.isOpen);
    } else {
      previewHead.setVisible(false);
    }

    if (shieldId > 0) {
      previewShield
        .setTexture(getShieldSpriteKey(shieldId))
        .setFrame(getShieldIdleFrame(Direction.DOWN))
        .setVisible(this.isOpen);
    } else {
      previewShield.setVisible(false);
    }

    if (weaponId > 0) {
      previewWeapon
        .setTexture(getWeaponSpriteKey(weaponId))
        .setFrame(getWeaponIdleFrame(Direction.DOWN))
        .setVisible(this.isOpen);
    } else {
      previewWeapon.setVisible(false);
    }
  }

  private buildPanel(opts: InvokePanelOptions): void {
    const { initialX, initialY, width, height } = opts;
    const panelWidth = width;
    const panelHeight = height;
    const panelX = initialX;
    const panelY = initialY;

    const bg = this.scene.add
      .rectangle(panelX, panelY, panelWidth, panelHeight, 0x111111, 0.9)
      .setOrigin(0.5)
      .setStrokeStyle(1, 0x666666)
      .setDepth(1100)
      .setScrollFactor(0)
      .setVisible(false);

    const colPadding = 12;
    const listColWidth = panelWidth * 0.35;
    const detailColWidth = panelWidth * 0.35;
    const previewColWidth = panelWidth * 0.3;
    const baseX = panelX - panelWidth / 2;
    const baseY = panelY - panelHeight / 2;
    const leftStartX = baseX + colPadding;
    const centerStartX = leftStartX + listColWidth + colPadding;
    const rightStartX = centerStartX + detailColWidth + colPadding;

    const listBg = this.scene.add
      .rectangle(
        leftStartX + listColWidth / 2,
        panelY,
        listColWidth,
        panelHeight - colPadding * 2,
        0x222233,
        0.8
      )
      .setOrigin(0.5)
      .setStrokeStyle(1, 0x555577)
      .setDepth(1100)
      .setScrollFactor(0)
      .setVisible(false);

    const title = this.scene.add
      .text(panelX, baseY + 16, 'Invocar NPC', withGameFont({
        fontSize: '16px',
        color: '#ffffff',
      }))
      .setOrigin(0.5, 0.5)
      .setDepth(1100)
      .setScrollFactor(0)
      .setVisible(false)
      .setInteractive({ useHandCursor: true });

    const listStartY = baseY + 36;
    const rowHeight = 14;
    const list: Phaser.GameObjects.Text[] = [];
    for (let i = 0; i < 24; i += 1) {
      const y = listStartY + i * rowHeight;
      const txt = this.scene.add
        .text(leftStartX + 6, y, '', withGameFont({
          fontSize: '12px',
          color: '#ffffff',
        }))
        .setOrigin(0, 0)
        .setInteractive({ useHandCursor: true })
        .setDepth(1101)
        .setScrollFactor(0)
        .setVisible(false);

      const index = i;
      txt.on('pointerup', (pointer: Phaser.Input.Pointer) => {
        this.callbacks.onSelect(index);
        pointer.event.stopPropagation();
      });

      list.push(txt);
    }

    const nameText = this.scene.add
      .text(centerStartX, listStartY, '', withGameFont({
        fontSize: '14px',
        color: '#ffffff',
        fontStyle: 'bold',
        wordWrap: { width: detailColWidth - colPadding * 2 },
      }))
      .setOrigin(0, 0)
      .setDepth(1100)
      .setScrollFactor(0)
      .setVisible(false);

    const detailText = this.scene.add
      .text(centerStartX, listStartY + 18, 'Selecciona un NPC', withGameFont({
        fontSize: '12px',
        color: '#ffffff',
        wordWrap: { width: detailColWidth - colPadding * 2 },
      }))
      .setOrigin(0, 0)
      .setDepth(1100)
      .setScrollFactor(0)
      .setVisible(false);

    const invokeButton = this.scene.add
      .text(centerStartX + detailColWidth / 2, panelY + panelHeight / 2 - 32, '[ Invocar ]', withGameFont({
        fontSize: '14px',
        color: '#ffffaa',
        backgroundColor: '#333333',
      }))
      .setOrigin(0.5, 0.5)
      .setPadding(8, 4, 8, 4)
      .setInteractive({ useHandCursor: true })
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false);

    invokeButton.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.callbacks.onInvoke();
      pointer.event.stopPropagation();
    });

    const previewCenterX = rightStartX + previewColWidth / 2;
    const previewBox = this.scene.add
      .rectangle(previewCenterX, panelY, 128, 128, 0x000000, 0.8)
      .setOrigin(0.5)
      .setStrokeStyle(1, 0xffffff)
      .setDepth(1100)
      .setScrollFactor(0)
      .setVisible(false);

    const previewBody = this.scene.add
      .sprite(previewCenterX, panelY, getBodySpriteKey(1))
      .setOrigin(0.5, 0.5)
      .setVisible(false)
      .setDepth(1100)
      .setScrollFactor(0);

    const bodyBottomY = panelY + 64 / 2;
    const headCenterY = bodyBottomY - 27 - 30 / 2;

    const previewHead = this.scene.add
      .sprite(previewCenterX, headCenterY, getHeadSpriteKey(1))
      .setOrigin(0.5, 0.5)
      .setVisible(false)
      .setDepth(1101)
      .setScrollFactor(0);

    const previewShield = this.scene.add
      .sprite(previewCenterX - 4, panelY, getShieldSpriteKey(1))
      .setOrigin(0.5, 1)
      .setVisible(false)
      .setDepth(1101)
      .setScrollFactor(0);

    const previewWeapon = this.scene.add
      .sprite(previewCenterX - 1, panelY, getWeaponSpriteKey(1))
      .setOrigin(0.5, 1)
      .setVisible(false)
      .setDepth(1101)
      .setScrollFactor(0);

    const closeButton = this.scene.add
      .text(panelX + panelWidth / 2 - 16, panelY - panelHeight / 2 + 8, '✕', withGameFont({
        fontSize: '16px',
        color: '#ff6666',
      }))
      .setOrigin(0.5, 0)
      .setInteractive({ useHandCursor: true })
      .setDepth(1102)
      .setScrollFactor(0)
      .setVisible(false);

    closeButton.on('pointerover', () => closeButton.setColor('#ff9999'));
    closeButton.on('pointerout', () => closeButton.setColor('#ff6666'));
    closeButton.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.close();
      pointer.event.stopPropagation();
    });

    title.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.isDragging = true;
      this.dragOffsetX = pointer.x - panelX;
      this.dragOffsetY = pointer.y - panelY;
      pointer.event.stopPropagation();
    });

    this.elements = {
      bg,
      listBg,
      title,
      list,
      nameText,
      detailText,
      invokeButton,
      previewBox,
      previewBody,
      previewHead,
      previewShield,
      previewWeapon,
      closeButton,
      panelX,
      panelY,
      panelWidth,
      panelHeight,
    };
  }

  private setVisible(visible: boolean): void {
    if (!this.elements) return;
    const panel = this.elements;
    panel.bg.setVisible(visible).setActive(visible);
    panel.listBg.setVisible(visible).setActive(visible);
    panel.title.setVisible(visible).setActive(visible);
    panel.list.forEach((txt) => txt.setVisible(visible).setActive(visible));
    panel.nameText.setVisible(visible).setActive(visible);
    panel.detailText.setVisible(visible).setActive(visible);
    panel.invokeButton.setVisible(visible).setActive(visible);
    panel.previewBox.setVisible(visible).setActive(visible);
    panel.previewBody.setVisible(visible).setActive(visible);
    panel.previewHead.setVisible(visible).setActive(visible);
    panel.previewShield.setVisible(visible).setActive(visible);
    panel.previewWeapon.setVisible(visible).setActive(visible);
    panel.closeButton.setVisible(visible).setActive(visible);
    this.isOpen = visible;
  }

  private move(newX: number, newY: number): void {
    if (!this.elements) return;
    const panel = this.elements;
    panel.panelX = newX;
    panel.panelY = newY;

    const { panelWidth, panelHeight, list, listBg, title, closeButton } = panel;
    panel.bg.setPosition(newX, newY);
    listBg.setPosition(newX - panelWidth / 2 + panelWidth * 0.35 / 2 + 12, newY);
    title.setPosition(newX, newY - panelHeight / 2 + 16);
    closeButton.setPosition(newX + panelWidth / 2 - 16, newY - panelHeight / 2 + 8);

    const colPadding = 12;
    const listColWidth = panelWidth * 0.35;
    const detailColWidth = panelWidth * 0.35;
    const previewColWidth = panelWidth * 0.3;
    const baseX = newX - panelWidth / 2;
    const baseY = newY - panelHeight / 2;
    const leftStartX = baseX + colPadding;
    const centerStartX = leftStartX + listColWidth + colPadding;
    const rightStartX = centerStartX + detailColWidth + colPadding;

    const listStartY = baseY + 36;
    const rowHeight = 18;
    list.forEach((txt, i) => {
      const y = listStartY + i * rowHeight;
      txt.setPosition(leftStartX + 6, y);
    });

    panel.nameText.setPosition(centerStartX, listStartY);
    panel.detailText.setPosition(centerStartX, listStartY + 18);
    panel.invokeButton.setPosition(centerStartX + detailColWidth / 2, newY + panelHeight / 2 - 32);

    const previewCenterX = rightStartX + previewColWidth / 2;
    panel.previewBox.setPosition(previewCenterX, newY);
    panel.previewBody.setPosition(previewCenterX, newY);

    const bodyBottomY = newY + 64 / 2;
    const headCenterY = bodyBottomY - 27 - 30 / 2;
    panel.previewHead.setPosition(previewCenterX, headCenterY);
    panel.previewShield.setPosition(previewCenterX - 4, newY);
    panel.previewWeapon.setPosition(previewCenterX - 1, newY);
  }
}

